const phases = new Set(['plan', 'summary'])

/**
 * A submitted plan can be adjusted only until the summary is submitted.
 * The backend does not yet guarantee preservation of submitted summary fields
 * during a later plan adjustment, so the frontend keeps the plan read-only.
 */
export function canAdjustPlan(report) {
  return report?.planStatus === 'SUBMITTED' && report?.summaryStatus !== 'SUBMITTED'
}

/**
 * A summary response replaces the report DTO, so a pending plan adjustment
 * must be submitted or discarded before the summary may submit.
 */
export function canSubmitSummary(state) {
  return state?.planStatus === 'SUBMITTED' && !state?.planSubmittedEditDirty
}

/**
 * Summary inputs and draft saves must not mutate the report while a direct
 * plan adjustment is waiting to be submitted or discarded.
 */
export function canEditSummary(state) {
  return state?.planStatus === 'SUBMITTED' && !state?.planSubmittedEditDirty
}

export function canSaveSummary(state) {
  return canEditSummary(state)
}

/**
 * Starting a direct plan adjustment while the summary draft is pending would
 * allow a delayed summary save to replace the report DTO. Require the user to
 * settle the summary draft first.
 */
export function canStartPlanAdjustment(state) {
  return !state?.summaryHasUnsavedChanges && state?.summarySaveStatus !== 'saving'
}

/**
 * Track unsaved direct edits for already-submitted report phases.
 * These edits cannot use the draft endpoints, so each phase must retain
 * its own dirty flag until that phase is submitted or discarded.
 */
export function createWorkReportSubmittedEditStateMachine() {
  let planSubmittedEditDirty = false
  let summarySubmittedEditDirty = false

  function assertPhase(phase) {
    if (!phases.has(phase)) {
      throw new Error(`unsupported work report phase: ${phase}`)
    }
  }

  function markDirty(phase) {
    assertPhase(phase)
    if (phase === 'plan') planSubmittedEditDirty = true
    else summarySubmittedEditDirty = true
  }

  function submitSucceeded(phase) {
    clear(phase)
  }

  function discard(phase) {
    clear(phase)
  }

  function clear(phase) {
    assertPhase(phase)
    if (phase === 'plan') planSubmittedEditDirty = false
    else summarySubmittedEditDirty = false
  }

  function reset() {
    planSubmittedEditDirty = false
    summarySubmittedEditDirty = false
  }

  function getState() {
    return {
      planSubmittedEditDirty,
      summarySubmittedEditDirty,
      hasUnsavedChanges: planSubmittedEditDirty || summarySubmittedEditDirty
    }
  }

  return {
    markDirty,
    submitSucceeded,
    discard,
    reset,
    getState
  }
}
